import { loadStripe, Stripe, StripeElementStyle } from '@stripe/stripe-js';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Skeleton } from 'components/app-components/ui/skeleton';
import theraToast from 'components/generic-components/toast/TheraToast';
import { formatErrorMessage } from 'utils/error';

type MountedStripeElement = {
  destroy: () => void;
};

type StripeWithIssuingCardRetrieve = Stripe & {
  retrieveIssuingCard: (
    issuingCard: string,
    options: {
      ephemeralKeySecret: string;
      nonce: string;
    },
  ) => Promise<{
    error?: {
      message?: string;
    };
  }>;
};

interface StripeIssuingCardDetailsDisplayProps {
  theraFernApi: any;
  userId?: string;
  stripeCardId: string;
  last4?: string;
  showDetails: boolean;
  onToggleDetails: () => void;
  userName: string;
  addressToDisplay: string;
}

const issuingElementStyle: StripeElementStyle = {
  base: {
    color: '#ffffff',
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: '16px',
    fontWeight: '500',
    lineHeight: '20px',
  },
};

const issuingSmallElementStyle: StripeElementStyle = {
  base: {
    ...issuingElementStyle.base,
    fontSize: '14px',
    lineHeight: '18px',
  },
};

export const StripeIssuingCardDetailsDisplay = ({
  theraFernApi,
  userId,
  stripeCardId,
  last4,
  showDetails,
  onToggleDetails,
  userName,
  addressToDisplay,
}: StripeIssuingCardDetailsDisplayProps) => {
  const numberRef = useRef<HTMLDivElement | null>(null);
  const expiryRef = useRef<HTMLDivElement | null>(null);
  const cvcRef = useRef<HTMLDivElement | null>(null);
  const mountedElementsRef = useRef<MountedStripeElement[]>([]);
  const [areDetailsMounted, setAreDetailsMounted] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  const stripePromise = useMemo(() => {
    const publishableKey =
      process.env.NEXT_PUBLIC_STRIPE_ISSUING_PUBLISHABLE_KEY || process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
    if (!publishableKey) return null;
    return loadStripe(publishableKey, {
      betas: ['issuing_add_to_wallet_button_element_1', 'issuing_elements_2'],
    });
  }, []);

  useEffect(() => {
    if (!showDetails || !userId || stripePromise === null || areDetailsMounted) return undefined;

    let isCancelled = false;
    const mountedElements: MountedStripeElement[] = [];

    const mountStripeIssuingElements = async () => {
      try {
        setIsLoadingDetails(true);
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Stripe failed to initialize');
        }

        const nonceResult = await stripe.createEphemeralKeyNonce({
          issuingCard: stripeCardId,
        });
        if (nonceResult.error || !nonceResult.nonce) {
          throw new Error(nonceResult.error?.message || 'Failed to create Stripe card nonce');
        }

        const ephemeralKeyResponse = await theraFernApi.thera.bridge.bridgeApi.createStripeCardEphemeralKey(
          userId,
          stripeCardId,
          {
            nonce: nonceResult.nonce,
          },
        );

        if (isCancelled || !numberRef.current || !expiryRef.current || !cvcRef.current) {
          return;
        }

        const elements = stripe.elements();
        const cardResult = await (stripe as StripeWithIssuingCardRetrieve).retrieveIssuingCard(stripeCardId, {
          ephemeralKeySecret: ephemeralKeyResponse.ephemeralKeySecret,
          nonce: nonceResult.nonce,
        });
        if (cardResult.error) {
          throw new Error(cardResult.error.message || 'Failed to retrieve Stripe card');
        }
        if (isCancelled || !numberRef.current || !expiryRef.current || !cvcRef.current) {
          return;
        }

        const cardElementOptions = {
          issuingCard: stripeCardId,
          nonce: nonceResult.nonce,
          ephemeralKeySecret: ephemeralKeyResponse.ephemeralKeySecret,
        };

        const number = elements.create('issuingCardNumberDisplay', {
          ...cardElementOptions,
          style: issuingElementStyle,
        });
        const expiry = elements.create('issuingCardExpiryDisplay', {
          ...cardElementOptions,
          style: issuingSmallElementStyle,
        });
        const cvc = elements.create('issuingCardCvcDisplay', {
          ...cardElementOptions,
          style: issuingSmallElementStyle,
        });
        number.mount(numberRef.current);
        expiry.mount(expiryRef.current);
        cvc.mount(cvcRef.current);
        mountedElements.push(number, expiry, cvc);
        mountedElementsRef.current = mountedElements;
        setAreDetailsMounted(true);
      } catch (error) {
        mountedElements.forEach((element) => element.destroy());
        if (!isCancelled) {
          const errorMessage = formatErrorMessage(error, 'Failed to fetch card details');
          theraToast.error({
            message: errorMessage,
            error,
            options: {
              id: 'failed-to-fetch-stripe-card-details',
            },
          });
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingDetails(false);
        }
      }
    };

    mountStripeIssuingElements();

    return () => {
      isCancelled = true;
    };
  }, [areDetailsMounted, showDetails, stripeCardId, stripePromise, theraFernApi, userId]);

  useEffect(() => {
    return () => {
      mountedElementsRef.current.forEach((element) => element.destroy());
      mountedElementsRef.current = [];
      setAreDetailsMounted(false);
    };
  }, [stripeCardId]);

  const canRevealDetails = !isLoadingDetails && Boolean(userId && stripePromise);
  // Only hide the masked values once the Stripe elements are mounted (or loading), so a failed reveal keeps the card readable
  const showRevealedDetails = showDetails && (areDetailsMounted || isLoadingDetails);

  return (
    <div className="flex flex-col items-start gap-4 sm:flex-row">
      <div className="relative w-full min-w-[240px] shrink-0 sm:w-auto">
        <div
          role="button"
          onClick={canRevealDetails ? onToggleDetails : undefined}
          className={`relative ${canRevealDetails ? 'cursor-pointer' : ''}`}
        >
          <img src="/images/thera-card-bg.svg" alt="thera-card" className="w-full min-w-[240px] max-w-[320px]" />
          <div id="card-details" className="absolute bottom-4 left-4">
            <div className="flex flex-col gap-0.5">
              <div className="flex min-h-5 items-center gap-2">
                <div className={`${showRevealedDetails ? 'flex' : 'hidden'} items-center gap-2`}>
                  <div className="relative h-5 w-48">
                    <div ref={numberRef} className={`h-5 w-48 ${isLoadingDetails ? 'opacity-0' : ''}`} />
                    {isLoadingDetails && <Skeleton className="absolute inset-0 h-4 w-40 bg-neutral-500" />}
                  </div>
                </div>
                {!showRevealedDetails && (
                  <p className="text-base font-medium tabular-nums text-white">•••• •••• •••• {last4 || '----'}</p>
                )}
              </div>
            </div>
            <div className="mt-3 flex">
              <div className="flex flex-[2] flex-col gap-0.5">
                <p className="text-xs font-medium text-neutral-400">Expiry</p>
                <div className={`${showRevealedDetails ? 'flex' : 'hidden'} min-h-5 items-center gap-2`}>
                  <div className="relative h-5 w-14">
                    <div ref={expiryRef} className={`h-5 w-14 ${isLoadingDetails ? 'opacity-0' : ''}`} />
                    {isLoadingDetails && <Skeleton className="absolute inset-0 h-3 w-12 bg-neutral-500" />}
                  </div>
                </div>
                {!showRevealedDetails && <p className="text-sm font-medium text-white">••/••</p>}
              </div>
              <div className="flex flex-[3] flex-col gap-0.5">
                <p className="text-xs font-medium text-neutral-400">CVV</p>
                <div className={`${showRevealedDetails ? 'flex' : 'hidden'} min-h-5 items-center gap-2`}>
                  <div className="relative h-5 w-12">
                    <div ref={cvcRef} className={`h-5 w-12 ${isLoadingDetails ? 'opacity-0' : ''}`} />
                    {isLoadingDetails && <Skeleton className="absolute inset-0 h-3 w-12 bg-neutral-500" />}
                  </div>
                </div>
                {!showRevealedDetails && <p className="text-sm font-medium tabular-nums text-white">•••</p>}
              </div>
            </div>
          </div>
        </div>
        {!isLoadingDetails && (
          <p className="mt-2 text-xs text-neutral-500">
            {showRevealedDetails ? 'Click card to hide details' : 'Click card to reveal details'}
          </p>
        )}
        <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-neutral-600 bg-neutral-700 px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-therablue" />
          <span className="text-xs font-medium text-neutral-300">Physical cards coming soon</span>
        </div>
      </div>
      <div className="flex w-full flex-col gap-4 sm:max-w-[200px]">
        <div className="flex flex-col items-start gap-1 text-xs">
          <p className="text-xs font-medium text-neutral-500">Name</p>
          <p className="text-sm font-medium text-neutral-200">{userName}</p>
        </div>
        <div className="flex flex-col items-start gap-1 text-xs">
          <p className="text-xs font-medium text-neutral-500">Address</p>
          <p className="text-sm font-medium text-neutral-200">{addressToDisplay}</p>
        </div>
      </div>
    </div>
  );
};
